import { useState, useEffect, useRef } from "react";
import { useHeaderContext } from "../context/headerContext";
import { useThemeContext } from "../context/themeContext";
import { useDrawerContext } from "../context/drawerContext";
import { useNavigate } from "react-router";
import EditIcon from "/public/images/icons/editPencil.svg?react";
import Button from "../components/Button";
import CloseIcon from "/public/images/icons/close.svg?react";
import TextInput from "../components/TextInput";
import { useUserContext } from "../context/userContext";
import { passwordStrength } from 'check-password-strength'
import { Link } from "react-router-dom";
import AzureProfilePictureUpload from "../components/AzureProfilePictureUpload";
import fetchWithRefresh from "../utils/fetchWithRefresh";
import UpgradeAvailable from "../components/UpgradeAvailable";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { format } from 'date-fns';
import { LogErrors } from "../utils/LogErrors";

export default function Account() {
  const { title, setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { setTheme } = useThemeContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { logout, updateTokens, user, selectedVenue, refreshSubscription } = useUserContext();
  const [ errors, setErrors ] = useState({});
  const [ newPassword, setNewPassword ] = useState(false);
  const [ passStrength, setPassStrength ] = useState('Weak');
  const [ password, setPassword ] = useState('');
  const [ activeAccount, setActiveAccount ] = useState({});
  const [ fetchingAccount, setFetchingAccount ] = useState(true);
  const [ userNameUpdated, setUserNameUpdated ] = useState(false);
  const [ userPhoneUpdated, setUserPhoneUpdated ] = useState(false);
  const [ userEmailUpdated, setUserEmailUpdated ] = useState(false);
  const [ loading, setLoading ] = useState(true);
  const [ confirmationSent, setConfirmationSent ] = useState(null);
  const [ smsSending, setSmsSending ] = useState(false);
  const [ otpSubmitting, setOtpSubmitting ] = useState(false);
  const [ otpDrawerActive, setOtpDrawerActive ] = useState(false);
  const [ passwordDrawerActive, setPasswordDrawerActive ] = useState(false);
  const [ userName, setUserName ] = useState(activeAccount?.FirstName + " " + activeAccount?.LastName || "");
  const [ userPhone, setUserPhone ] = useState(activeAccount?.PhoneNumber || "");
  const [ userEmail, setUserEmail ] = useState(activeAccount?.Email || "");
  const [ redeemBandDetails, setRedeemBandDetails ] = useState(null);
  const [ passTryCount, setPassTryCount ] = useState(0);
  const [ userPhoneMasked, setUserPhoneMasked ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setTitle("Account");
    setCartButton(true);
    setBackButton(true);

    const handleBack = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleBack);
  }, []);

  const handleOrderBandClick = async () => {
    const freshSubscription = await refreshSubscription();
    if (freshSubscription?.Name === 'Lite') {
      window.open(freshSubscription?.LiteShopUrl || '/shop/my-participants', '_blank');
    }
  };

  const changeNameDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            EDIT NAME
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false); userNameUpdated && setUserNameUpdated(false);}} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Edit your Name</h3> 
          <form className="flex flex-col gap-6" onSubmit={(e) => updateAccountUserName(e)} name="usernameform">
            <TextInput key='name' label="Name" name="name" defaultValue={userName} />
            <Button submit className="!text-[#101820]">
              Confirm & Continue
            </Button>
          </form>
        </div>
      </div>
    )
  }

  const updateAccountUserName = async (event) => {
    event.preventDefault();
    setErrors({});
    setUserNameUpdated(false);
  
    return await Sentry.startSpan({ name: "Update Account Username", op: "http.request" }, async (span) => {
      try {
        const newDetails = new FormData(event.currentTarget);
        const fullName = newDetails.get("name");
        const [newFirstName, ...rest] = fullName.split(" ");
        const newLastName = rest.join(" ");
  
        const url = `${import.meta.env.VITE_API_URL}Users/UpdateAccountUserName`;
        span.setAttribute("http.url", url);
        span.setAttribute("userLoginId", user?.id);
  
        const req = await fetchWithRefresh(url, {
          method: "PUT",
          body: JSON.stringify({
            userLoginId: user?.id,
            firstName: newFirstName,
            lastName: newLastName,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message);});
  
        if (req.status === 200) {
          span.setStatus("ok");
          window.location.reload();
        } else {
          const errorMessage = `User name failed to update. Status: ${req.status}`;
          Sentry.captureMessage(errorMessage, "warning");
          span.setStatus("error");
          LogErrors(user, logout, updateTokens, errorMessage);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { userToken: user.token } });
        LogErrors(user, logout, updateTokens, "Error updating username: " + error);
      }
    });
  };

  const changeTelephoneDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            EDIT TELEPHONE
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false); userPhoneUpdated && setUserPhoneUpdated(false);}} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Edit your Telephone</h3>
            <form className="flex flex-col gap-6" onSubmit={(e) => updateAccountTelephone(e)} name="userphoneform">
              <TextInput type="text" key='tel' label="Telephone" name="telephone" defaultValue={userPhone} />
              <Button submit className="!text-[#101820]">
                Confirm & Continue
              </Button>
            </form>
        </div>
      </div>
    )
  }

  const updateAccountTelephone = async (event) => {
    event.preventDefault();
    setErrors({});
    setUserPhoneUpdated(false);
  
    return await Sentry.startSpan({ name: "Update Account Telephone", op: "http.request" }, async (span) => {
      try {
        const newDetails = new FormData(event.currentTarget);
        const phoneNumber = newDetails.get("telephone");
  
        const url = `${import.meta.env.VITE_API_URL}Users/UpdateAccountTelephone/`;
        span.setAttribute("http.url", url);
        span.setAttribute("userLoginId", user?.id);
        span.setAttribute("phoneNumber", phoneNumber);
  
        const req = await fetchWithRefresh(url, {
          method: "PUT",
          body: JSON.stringify({
            userLoginId: user?.id,
            phoneNumber: phoneNumber
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message);});
  
        if (req.status === 200) {
          span.setStatus("ok");
          window.location.reload();
        } else {
          const errorMessage = `Telephone failed to update. Status: ${req.status}`;
          Sentry.captureMessage(errorMessage, "warning");
          span.setStatus("error");
          LogErrors(user, logout, updateTokens, errorMessage);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { userToken: user.token } });
        LogErrors(user, logout, updateTokens, "Error updating telephone: " + error);
      }
    });
  };

  const changeEmailDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            EDIT EMAIL
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false); userEmailUpdated && setUserEmailUpdated(false);}} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Edit your Email</h3>        
          
          <form className="flex flex-col gap-6" onSubmit={(e) => updateAccountEmail(e)}>
            <TextInput type="text" key='email' label="Email" name="email" defaultValue={userEmail} />
            <Button submit className="!text-[#101820]">
              Confirm & Continue
            </Button>
          </form>
          
        </div>
      </div>
    )
  }

const updateAccountEmail = async (event) => {
  event.preventDefault();
  setErrors({});
  setUserEmailUpdated(false);

  return await Sentry.startSpan({ name: "Update Account Email", op: "http.request" }, async (span) => {
    try {
      const newDetails = new FormData(event.currentTarget);
      const email = newDetails.get("email");

      const url = `${import.meta.env.VITE_API_URL}Users/UpdateAccountEmail/`;
      span.setAttribute("http.url", url);
      span.setAttribute("userLoginId", user?.id);
      span.setAttribute("email", email);

      const req = await fetchWithRefresh(url, {
        method: "PUT",
        body: JSON.stringify({
          userLoginId: user?.id,
          email: email,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message);});

      if (req.status === 200) {
        span.setStatus("ok");
        window.location.reload();
      } else {
        const errorMessage = `Email update failed. Status: ${req.status}`;
        Sentry.captureMessage(errorMessage, "warning");
        span.setStatus("error");
        LogErrors(user, logout, updateTokens, errorMessage);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { userToken: user.token } });
      LogErrors(user, logout, updateTokens, "Error updating email: " + error);
    }
  });
};

const otpLogin = async (event, email) => {
  event.preventDefault();
  setOtpSubmitting(true);
  setErrors({});

  return await Sentry.startSpan({ name: "OTP Login", op: "http.request" }, async (span) => {
    try {
      const newDetails = new FormData(event.currentTarget);
      const otp = newDetails.get("confirmation");

      const url = `${import.meta.env.VITE_API_URL}Users/LoginOTP/`;
      span.setAttribute("http.url", url);
      span.setAttribute("email", email);

      const req = await fetchWithRefresh(url, {
        method: "POST",
        body: JSON.stringify({ email, otp }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens);

      if (req.status === 401) {
        const remainingAttempts = 3 - passTryCount;
        const errorMessage = passTryCount >= 3 
          ? "Incorrect OTP. Maximum attempts reached, please resend the confirmation code."
          : `Incorrect OTP. Please try again. ${remainingAttempts} ${remainingAttempts === 1 ? "Attempt" : "Attempts"} Remaining.`;

        Sentry.captureMessage(errorMessage, "warning");
        setErrors({ confirmation: errorMessage });
        setPassTryCount(passTryCount + 1);
        span.setStatus("error");
      } else if (req.status === 200) {
        toast.success("OTP verified successfully.");
        span.setStatus("ok");
        setOtpDrawerActive(false);
        passwordStrengthDrawer();
      } else {
        const errorMessage = `Unexpected response status: ${req.status}`;
        Sentry.captureMessage(errorMessage, "error");
        LogErrors(user, logout, updateTokens, errorMessage);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { email, userToken: user?.token } });
      LogErrors(user, logout, updateTokens, "Error during OTP login: " + error);
      toast.error("An error occurred while verifying OTP.");
    } finally {
      setOtpSubmitting(false);
    }
  });
};

  useEffect(() => {
    if (passwordDrawerActive && errors.newPassword) {
      setDrawerContent(buildPasswordDrawerContent(password, passStrength));
    }
  }, [errors])

  const buildPasswordDrawerContent = (pw = password, strength = passStrength) => (
    <div className="w-full change-password">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          CHANGE PASSWORD
        </h2>
        <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <div className="mt-6 flex flex-col justify-stretch">
        <form className="flex flex-col gap-4" onSubmit={setPass}>
          <TextInput
            type={"password"}
            key={"newPassword"}
            error={errors.newPassword}
            onChange={handlePasswordChange}
            name="newPassword"
            label="Enter Your New Password"
            placeholder="Enter Password"
          />
          <div className="border-[1px] border-[#E3E3E3] flex flex-col gap-5 p-5">
            <p className="font-medium">Password Must Contain:</p>
            <div>
              <ul className="flex flex-col gap-1">
                <li className="flex items-center gap-2"><input readOnly type='checkbox' name='minChar' className={`checkbox rounded-full passwordCheck`} checked={pw.length >= 8}  /> 8 characters minimum</li>
                <li className="flex items-center gap-2"><input readOnly type='checkbox' name='number' className={`checkbox rounded-full passwordCheck`} checked={/\d/.test(pw)}  /> At least 1 number</li>
                <li className="flex items-center gap-2"><input readOnly type='checkbox' name='uppercase' className={`checkbox rounded-full passwordCheck`} checked={/[A-Z]/.test(pw)}  /> At least 1 uppercase letter</li>
                <li className="flex items-center gap-2"><input readOnly type='checkbox' name='lowercase' className={`checkbox rounded-full passwordCheck`} checked={/[a-z]/.test(pw)}  /> At least 1 lowercase letter</li>
                <li className="flex items-center gap-2"><input readOnly type='checkbox' name='specChar' className={`checkbox rounded-full passwordCheck`} checked={/[!@#$%^&*(),.?":{}|<>]/.test(pw)}  /> At least 1 special character</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Strength:</p>
              <div className="flex gap-1 mt-5">
                <div className={`${strength === 'Too weak' ? 'bg-red-500' : strength === 'Weak' || strength === 'Medium' ? 'bg-yellow-500' : strength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`} />
                <div className={`${strength === 'Weak' || strength === 'Medium' ? 'bg-yellow-500' : strength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`}  />
                <div className={`${strength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`}  />
              </div>
            </div>
          </div>
          <TextInput
            type={"password"}
            key={"newPassword2"}
            error={errors.newPassword2}
            name="newPassword2"
            label="Re-Enter Your New Password"
            placeholder="Enter Password"
          />
          <Button submit>Continue</Button>
        </form>
      </div>
    </div>
  );

  const passwordStrengthDrawer = () => {
    setOtpDrawerActive(false);
    setPasswordDrawerActive(true);
    setOpen(true);
    setDrawerContent(buildPasswordDrawerContent());
  }

  const resendConfirmation = async () => {
    setPassTryCount(0);
    setConfirmationSent(true);
    setTimeout(() => { setConfirmationSent(false); }, 5000);
  
    return await Sentry.startSpan({ name: "Resend OTP", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Users/GenerateOTP/`;
        span.setAttribute("http.url", url);
        span.setAttribute("email", activeAccount?.Email);
  
        const response = await fetchWithRefresh(url, {
          method: "POST",
          body: JSON.stringify({ email: activeAccount?.Email }),
          headers: { "Content-Type": "application/json" },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (!response.ok) {
          Sentry.captureMessage("Failed to resend OTP", "warning");
          LogErrors(user, logout, updateTokens, "Error resending OTP");
          toast.error("Failed to resend OTP. Please try again.");
        } else {
          toast.success("Confirmation code sent.");
        }
  
        span.setStatus(response.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { email: activeAccount?.Email } });
        LogErrors(user, logout, updateTokens, "Error resending OTP: " + error);
      }
    });
  };

  const setPass = async (event) => {
    event.preventDefault();
    setErrors({});
    const newDetails = new FormData(event.currentTarget);
  
    if (newDetails.get("newPassword") !== newDetails.get("newPassword2")) {
      return setErrors({ newPassword2: "Password doesn't match. Try again" });
    }
  
    const isPasswordValid = () => {
      const password = newDetails.get("newPassword");
      return password.length >= 8 &&
        /\d/.test(password) &&
        /[A-Z]/.test(password) &&
        /[a-z]/.test(password) &&
        /[!@#$%^&*(),.?":{}|<>]/.test(password);
    };
  
    if (!isPasswordValid()) {
      return setErrors({ newPassword: "Password does not meet the requirements" });
    }
  
    return await Sentry.startSpan({ name: "Set Password", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Users/ChangePassword/`;
        span.setAttribute("http.url", url);
        span.setAttribute("email", activeAccount?.Email);
  
        const response = await fetchWithRefresh(url, {
          method: "POST",
          body: JSON.stringify({
            email: activeAccount?.Email,
            password: newDetails.get("newPassword"),
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.status === 401) {
          setErrors({ newPassword: "Password is incorrect. Please try again" });
          Sentry.captureMessage("Incorrect password attempt", "warning");
        } else if (response.status === 200) {
          setOpen(false);
          setNewPassword(true);
          logout();
        } else {
          Sentry.captureMessage(`Unexpected response status: ${response.status}`, "error");
        }
  
        span.setStatus(response.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { email: activeAccount?.Email } });
        LogErrors(user, logout, updateTokens, "Error setting password: " + error);
      }
    });
  };  

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    const newStrength = passwordStrength(newPassword).value;
    setPassword(newPassword);
    setPassStrength(newStrength);
    setDrawerContent(buildPasswordDrawerContent(newPassword, newStrength));
  };

  const fetchUserPhoneMasked = async (emailAddress) => {
    return await Sentry.startSpan({ name: "Fetch User Phone Masked (Account)", op: "http.request" }, async (span) => {
      try {
        const targetEmail = emailAddress || activeAccount?.Email;
        span.setAttribute("userLoginId", user?.id || "unknown");
        span.setAttribute("email", targetEmail || "unknown");

        if (!targetEmail) return;

        const req = await fetch(import.meta.env.VITE_API_URL + `Users/FetchUserPhoneMasked`, {
          method: "POST",
          body: JSON.stringify({
            email: targetEmail,
            mode: "last4",
          }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!req.ok) {
          const errorMessage = `Failed to fetch user phone. Status: ${req.status}`;
          Sentry.captureMessage(errorMessage, "warning");
          return;
        }

        const res = await req.json();
        const last4 = res?.last4 || res?.Last4 || res?.ladt4 || res?.masked || res?.phoneMasked || res?.PhoneMasked || "";
        setUserPhoneMasked(String(last4));
        span.setStatus("ok");
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error in fetchUserPhoneMasked (account): " + error);
        Sentry.captureException(error, { extra: { userLoginId: user?.id, email: emailAddress } });
      } finally {
        span.end();
      }
    });
  }

  const generateOtpBySMS = async (emailAddress) => {
    setSmsSending(true);
    return await Sentry.startSpan({ name: "Generate OTP By SMS (Account)", op: "http.request" }, async (span) => {
      try {
        const targetEmail = emailAddress || activeAccount?.Email;
        if (!targetEmail) {
          toast.error("Unable to send SMS OTP. Missing user details.");
          return;
        }

        const url = `${import.meta.env.VITE_API_URL}Users/GenerateOTPBySMS/`;
        span.setAttribute("http.url", url);
        span.setAttribute("userLoginId", user?.id || "");
        span.setAttribute("email", targetEmail);

        const req = await fetch(url, {
          method: "POST",
          body: JSON.stringify({
            userLoginId: String(user?.id || ""),
            email: targetEmail,
          }),
          headers: { "Content-Type": "application/json" },
        });

        if (!req.ok) {
          const errorMessage = `Failed to send OTP by SMS: ${req.status}`;
          Sentry.captureMessage(errorMessage, "warning");
          toast.error("Failed to send SMS OTP. Please try again.");
          return;
        }

        toast.success("SMS OTP sent successfully.");
        span.setStatus("ok");
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error generating OTP by SMS (account): " + error);
        Sentry.captureException(error, { extra: { userLoginId: user?.id, email: emailAddress } });
        toast.error("An error occurred while sending SMS OTP.");
      } finally {
        span.end();
        setSmsSending(false);
      }
    });
  }

  const buildOtpDrawerContent = () => (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          ONE TIME PASSWORD
        </h2>
        <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <div className="mt-6 flex flex-col gap-2 justify-stretch">
        <h3 className="text-[24px]">Enter your confirmation code</h3>
        <p className=" text-sportsDarkGrey">
          A new confirmation code has been sent to{" "}
          {activeAccount?.Email}
        </p>
        <form
          className="flex flex-col gap-2 justify-stretch"
          onSubmit={(e) => otpLogin(e, activeAccount?.Email)}
        >
          <TextInput
            label={"Confirmation Code"}
            name="confirmation"
            error={errors?.confirmation}
            htmlError={errors?.confirmation?.includes("href")}
          />
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => resendConfirmation()}
              status="nobg"
              disabled={confirmationSent}
              variant="tertiary"
              className={`disabled:opacity-50`}
            >
              {confirmationSent
                ? <><span className="loading loading-spinner loading-xs" /> Sending...</>
                : "Resend confirmation code"
              }
            </Button>
            <Button submit disabled={otpSubmitting}>
              {otpSubmitting
                ? <><span className="loading loading-spinner loading-sm" /> Verifying...</>
                : "Continue"
              }
            </Button>
            {userPhoneMasked && (
              <>
                <div className='border-t border-[#E3E3E3] text-center relative'>
                  <p className='absolute left-[50%] translate-x-[-50%] top-[-10px] text-sportsBlack font-semibold text-[12px] bg-white px-[9px] tracking-[0.2em]'>Or</p>
                </div>
                <Button
                  variant="quaternary"
                  disabled={smsSending}
                  onClick={(e) => { e.preventDefault(); generateOtpBySMS(activeAccount?.Email); }}
                >
                  {smsSending
                    ? <><span className="loading loading-spinner loading-sm" /> Sending...</>
                    : `Text to mobile ending ${userPhoneMasked}`
                  }
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  const oneTimePasswordDrawer = () => { 
    if (!userPhoneMasked && activeAccount?.Email) {
      fetchUserPhoneMasked(activeAccount?.Email);
    }
    setOtpDrawerActive(true);
    setOpen(true);
    setDrawerContent(buildOtpDrawerContent());
  }

  useEffect(() => {
    if (errors?.confirmation) {
      oneTimePasswordDrawer();
    }
  }, [errors?.confirmation]);

  useEffect(() => {
    if (confirmationSent !== null) {
      setDrawerContent(buildOtpDrawerContent());
    }
  }, [confirmationSent]);

  useEffect(() => {
    if (otpDrawerActive) {
      setDrawerContent(buildOtpDrawerContent());
    }
  }, [smsSending, otpSubmitting, userPhoneMasked]);

  useEffect(() => {
    if (activeAccount?.Email) {
      fetchUserPhoneMasked(activeAccount?.Email);
    }
  }, [activeAccount?.Email]);

  const fetchUserAccountDetails = async () => {
    if (!activeAccount?.Email) setFetchingAccount(true);
    let accountEndpoint;
  
    if (localStorage.getItem("theme") === "staff") {
      accountEndpoint = `Users/FetchStaffAccountDetails?OrgId=${selectedVenue?.OrgID}&UserLoginId=${user?.id}`;
    } else {
      accountEndpoint = `Users/FetchUserAccountDetails?UserLoginId=${user?.id}`;
    }
  
    return await Sentry.startSpan({ name: "Fetch User Account Details", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}${accountEndpoint}`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
        span.setAttribute("org_id", selectedVenue?.OrgID);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const res = await req.json();
          setActiveAccount(res);
          if (!activeAccount?.Email) setFetchingAccount(false);
        } else {
          Sentry.captureMessage(`Failed to fetch account details: ${req.status}`, "warning");
          LogErrors(user, logout, updateTokens, "Could not fetch account details.");
        }
  
        span.setStatus(req.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id, org_id: selectedVenue?.OrgID } });
        LogErrors(user, logout, updateTokens, "Error fetching account details: " + error);
      }
    });
  };

  const fetchUserRedeemBandStatus = async () => {
  
    return await Sentry.startSpan({ name: "Fetch User Redeem Band Status", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Cart/AccountRedemptionStatus?UserLoginId=${user?.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
        span.setAttribute("org_id", selectedVenue?.OrgID);
  
        const req = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (req.status === 200) {
          const res = await req.json();
          setRedeemBandDetails(res);
        } else {
          Sentry.captureMessage(`Failed to fetch redeem band status: ${req.status}`, "warning");
          LogErrors(user, logout, updateTokens, "Could not redeem band status.");
        }
  
        span.setStatus(req.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id, org_id: selectedVenue?.OrgID } });
        LogErrors(user, logout, updateTokens, "Error fetching redeem band status: " + error);
      }
    });
  };

  useEffect(() => {
   if (user && !activeAccount?.Email) {
      fetchUserAccountDetails();
      fetchUserRedeemBandStatus();
    }
  }, [user, title])

  useEffect(() => {
    if (userEmailUpdated || userNameUpdated || userPhoneUpdated) {
      fetchUserAccountDetails();
    }
  }, [userEmailUpdated, userNameUpdated, userPhoneUpdated]);


  const contactModal = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            CONTACT
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Need some assistance?</h3>
          <p className="font-poppins text-sportsDarkGrey mb-[32px]">We&apos;re happy to assist with any query. Our offices are open Monday - Friday between 09:00 - 16:30.</p>
        </div>
      </div>
    )
  }

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchProducts = async () => {
    return await Sentry.startSpan({ name: "Fetch Stripe Products", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Stripe/FetchStripeProducts?query=metadata["TYPE"]:"SMS"`;
        span.setAttribute("http.url", url);
        span.setAttribute("user_id", user?.id);
  
        const response = await fetchWithRefresh(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
  
          // Filter out null products and then sort alphabetically by product name
          const sortedProducts = data?.data
            .filter(product => product !== null) // Filter out null products
            .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name alphabetically
  
          // Set the sorted products in state
          setProducts(sortedProducts);
        } else {
          const errorMessage = `Failed to fetch products: ${response.status} ${response.statusText}`;
          Sentry.captureMessage(errorMessage, "warning");
          LogErrors(user, logout, updateTokens, errorMessage);
        }
  
        span.setStatus(response.ok ? "ok" : "error");
      } catch (error) {
        Sentry.captureException(error, { extra: { user_id: user?.id } });
        LogErrors(user, logout, updateTokens, "Error fetching products: " + error);
      } finally {
        setLoading(false);
      }
    });
  };
  
  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if(selectedProduct){
      buySMSCreditsModal();
    }
  }, [selectedProduct]);

  const buySMSCreditsModal = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            PURCHASE SMS CREDITS
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Select the number of<br/>credits you want to buy</h3>
          {products?.length > 0 && (
            <div className="flex flex-col gap-6 mb-6">
              {products?.map((product, index) => {
                const tokens = product?.name.split(" ")[0];
                const price = (product?.default_price?.unit_amount_decimal/100).toFixed(2);
                const perToken = (price/tokens).toFixed(2);
                return (
                  <div 
                    className={`
                      px-3 py-[14px] border-[#E3E3E3] border rounded relative
                      ${selectedProduct?.name === product?.name && "outline-sportsYellow outline -outline-offset-2 outline-[2px]" }
                    `} 
                    onClick={() => setSelectedProduct(product)}
                    key={tokens + "-" + index}
                  >
                    <div className="flex gap-3 items-center">
                      <div 
                        className={`
                          border-[#E3E3E3] w-[50px] h-[50px] border bg-[#F8F8F8] rounded object-contain
                          ${selectedProduct?.name !== product?.name ? "grayscale" : "" }
                        `}
                        style={{ backgroundImage: `url(${product?.images[0]})` }}
                      ></div>
                      <div>
                        <p className="font-semibold font-poppins text-[#101820] text-[16px] leding-[20px] capitalize">{product?.name}</p>
                        <p className="font-poppins text-[12px] leading-[18px] text-[#727272]">£{price} (£{perToken} each)</p>
                      </div>
                    </div>
                    {selectedProduct?.name === product?.name && (
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-sportsYellow p-[6px] rounded-l">
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10.9992 19.8002C13.3331 19.8002 15.5714 18.8731 17.2218 17.2227C18.8721 15.5724 19.7992 13.3341 19.7992 11.0002C19.7992 8.66629 18.8721 6.42798 17.2218 4.77766C15.5714 3.12734 13.3331 2.2002 10.9992 2.2002C8.66531 2.2002 6.427 3.12734 4.77668 4.77766C3.12636 6.42798 2.19922 8.66629 2.19922 11.0002C2.19922 13.3341 3.12636 15.5724 4.77668 17.2227C6.427 18.8731 8.66531 19.8002 10.9992 19.8002ZM15.0769 9.5779C15.2773 9.37043 15.3882 9.09257 15.3857 8.80415C15.3832 8.51574 15.2675 8.23984 15.0635 8.03589C14.8596 7.83195 14.5837 7.71626 14.2953 7.71375C14.0068 7.71125 13.729 7.82212 13.5215 8.0225L9.89922 11.6448L8.47692 10.2225C8.26946 10.0221 7.99159 9.91125 7.70318 9.91375C7.41476 9.91626 7.13887 10.0319 6.93492 10.2359C6.73097 10.4398 6.61528 10.7157 6.61278 11.0042C6.61027 11.2926 6.72115 11.5704 6.92152 11.7779L9.12152 13.9779C9.3278 14.1841 9.60754 14.3 9.89922 14.3C10.1909 14.3 10.4706 14.1841 10.6769 13.9779L15.0769 9.5779Z" fill="white"/>
                        </svg>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <Button variant="primary" onClick={() => setOpen(false)}><Link to={`/checkout/?amount=${selectedProduct?.default_price?.unit_amount_decimal}&product=${selectedProduct?.name}&id=${selectedProduct?.id}`}>Confirm & Continue</Link></Button>
        </div>
      </div>
    )
  }

  useEffect(() => {
    setUserName(activeAccount?.FirstName + " " + activeAccount?.LastName || "");
    setUserPhone(activeAccount?.PhoneNumber || "");
    setUserEmail(activeAccount?.Email || "");
  }, [activeAccount]);

  return (
    <div>
      <div className="flex flex-col">
        {fetchingAccount ?
          <h2>Fetching Details...</h2>
        : (
          <>
            <div className="flex flex-col gap-y-[24px]">
                {localStorage.getItem("theme") === "staff" && (
                  <AzureProfilePictureUpload activeAccount={activeAccount} />
                )}

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row justify-between items-center align-middle">
                    <h2>{activeAccount?.FirstName} {activeAccount?.LastName}</h2>
                    <div className="w-[24px] h-[24px] flex cursor-pointer" onClick={changeNameDrawer}>
                      <EditIcon />
                    </div>
                  </div>
                  {userNameUpdated && <p className="text-sportsGreen">Name successfully updated.</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div>
                      <p className="font-poppins font-semibold">Telephone</p>
                      <p className="text-sportsDarkGrey underline">{activeAccount?.PhoneNumber}</p>
                    </div>
                    {localStorage.getItem("theme") !== "staff" && (
                      <div className="w-[24px] h-[24px] flex cursor-pointer" onClick={changeTelephoneDrawer}>
                        <EditIcon />
                      </div>
                    )}
                  </div>
                  {userPhoneUpdated && <p className="text-sportsGreen">Phone Number successfully updated.</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div>
                      <p className="font-poppins font-semibold">Email</p>
                      <p className="text-sportsDarkGrey underline">{activeAccount?.Email}</p>
                    </div>
                    {localStorage.getItem("theme") !== "staff" && (
                      <div className="w-[24px] h-[24px] flex cursor-pointer" onClick={changeEmailDrawer}>
                        <EditIcon />
                      </div>
                    )}
                  </div>
                  {userEmailUpdated && <p className="text-sportsGreen">Email successfully updated.</p>}
                </div>

                <div className="flex mb-[24px] flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div>
                      <p className="font-poppins font-semibold">Password</p>
                      <p className="text-sportsDarkGrey">•••••••••••••••</p>
                    </div>
                    <div className="w-[24px] h-[24px] flex cursor-pointer" onClick={() => {resendConfirmation(), oneTimePasswordDrawer()}}>
                      <EditIcon />
                    </div>
                  </div>
                  {newPassword && <p className="text-sportsGreen">Password successfully updated.</p>}
                </div>
            </div>

            {redeemBandDetails && (
              <div className="mb-6">
                {redeemBandDetails?.RedemptionAllowed > 0 ? (
                  <Button variant="primary" href="/account/redeem" className="w-full">
                    Redeem Free Band
                  </Button>
                ) : (
                  <>
                    <Button status="disabled" variant="tertiary" disabled className="w-full mb-1">
                      Redeem Free Band
                    </Button>
                    <p className="text-[#6F6F6F]">You’ll be able to redeem your next free band on or after {format(new Date(redeemBandDetails?.AllowanceRenewalDate), 'dd/MM/yyyy')}</p>
                  </>
                )}
              </div>
            )}
            
            {localStorage.getItem("theme") !== "staff" &&
              <Button
                variant="quaternary"
                onClick={user?.subscription?.Name === 'Lite' ? handleOrderBandClick : undefined}
                href={user?.subscription?.Name === 'Lite' ? undefined : '/shop/my-participants'}
                className="w-full"
              >
                {user?.subscription?.Name === 'Lite' ? 'Order Your Band' : 'Order New Band'}
              </Button>
            }

            {localStorage.getItem("theme") !== "staff" && user?.subscription?.Name !== "Lite" &&
            <div className="flex flex-col justify-between border-t border-dashed border-[#E3E3E3] mt-[24px] pt-[24px] mb-[24px]">
              <div>
                <p className="font-poppins font-semibold">
                  {localStorage.getItem("theme") === "staff" ? "Venue Incident SMS Remaining" : "SMS Credits Remaining"}
                </p>
                <p className="text-sportsDarkGrey">
                  {localStorage.getItem("theme") === "staff" ?
                    activeAccount?.SMSCreditsRemaining || 0
                  : activeAccount?.SMSCredits || 0}
                </p>
              </div>
            </div>
            }
            {localStorage.getItem("theme") !== "staff" && activeAccount?.Name !== "Bronze" && user?.subscription?.Name !== "Lite" &&
              <Button variant="quaternary" onClick={buySMSCreditsModal}>Purchase Additional Credits</Button>
            }

            {localStorage.getItem("theme") !== "staff" && user?.subscription?.Name !== "Lite" &&
              <>
                <div className="flex flex-col justify-between border-t border-dashed border-[#E3E3E3] mt-[24px] pt-[24px] mb-[24px]">
                  <div>
                    <p className="font-poppins font-semibold">Subscription</p>
                    <p className="text-sportsDarkGrey">
                      {activeAccount?.Name}
                    </p>
                  </div>
                </div>
                <UpgradeAvailable />
              </>
            }
          </>
        )}
      </div>
    </div>
  );
}
